import React from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const StudentResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const result = location.state?.selectedResult;

  if (!result) {
    return (
      <div className="min-h-screen bg-main-gradient flex items-center justify-center">
        <Card className="glass-card">
          <CardContent className="text-center py-12">
            <p className="text-white">No result data available</p>
            <Button onClick={() => navigate('/student')} className="btn-primary mt-4">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTextWithComments = (text, comments) => {
    if (!comments || comments.length === 0) return text;

    const sortedComments = [...comments].sort((a, b) => a.highlight_start - b.highlight_start);
    let result = [];
    let lastIndex = 0;

    sortedComments.forEach((comment, index) => {
      if (comment.highlight_start > lastIndex) {
        result.push(
          <span key={`text-${index}`}>
            {text.slice(lastIndex, comment.highlight_start)}
          </span>
        );
      }

      const highlightedText = text.slice(comment.highlight_start, comment.highlight_end);
      result.push(
        <span
          key={`highlight-${index}`}
          className="relative inline-block"
        >
          <span
            className="text-highlight"
            style={{ 
              backgroundColor: comment.highlight_color + '50', 
              borderBottomColor: comment.highlight_color 
            }}
          >
            {highlightedText}
          </span>
          <div className="absolute z-10 w-64 p-2 mt-2 text-sm bg-slate-800 border border-slate-600 rounded-lg shadow-lg opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            {comment.comment_text}
          </div>
        </span>
      );

      lastIndex = comment.highlight_end;
    });

    if (lastIndex < text.length) {
      result.push(
        <span key="text-end">{text.slice(lastIndex)}</span>
      );
    }

    return result;
  };

  const task1Comments = result.comments?.filter(c => c.task_num === 1) || [];
  const task2Comments = result.comments?.filter(c => c.task_num === 2) || [];

  return (
    <div className="min-h-screen bg-main-gradient">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-6">
            <Button
              onClick={() => navigate('/student')}
              variant="outline"
              className="text-white border-white hover:bg-white hover:text-blue-600 mr-4"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-white">
                Detailed Feedback
              </h1>
              <p className="text-blue-200">
                {result.test_title} • {result.candidate_name}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-3 space-y-6">
            {/* Test Information */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  Test Information
                  <Button className="btn-primary" data-testid="download-pdf">
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Candidate Name:</span>
                    <p className="text-white font-medium">{result.candidate_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Candidate No:</span>
                    <p className="text-white font-medium">{result.candidate_no}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Submitted:</span>
                    <p className="text-white font-medium">{formatDate(result.submitted_at)}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Published:</span>
                    <p className="text-white font-medium">{formatDate(result.published_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task 1 with Feedback */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white">
                  Task 1 Answer ({result.word_count_task1} words)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-800/50 rounded-lg p-6">
                  <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {renderTextWithComments(result.task1_answer, task1Comments)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task 2 with Feedback */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white">
                  Task 2 Answer ({result.word_count_task2} words)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-800/50 rounded-lg p-6">
                  <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {renderTextWithComments(result.task2_answer, task2Comments)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Instructor Feedback */}
            {result.instructor_summary && (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-white">Instructor Feedback</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-800/50 rounded-lg p-6">
                    <p className="text-slate-300 leading-relaxed">{result.instructor_summary}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar with Scores */}
          <div className="space-y-6">
            {/* Final Band Score */}
            <Card className="glass-card">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="band-score-card">
                    <div className="text-sm font-medium">Overall Writing Band</div>
                    <div className="text-4xl font-bold">{result.final_band}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task 1 Breakdown */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white text-lg">Task 1 Scores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Task Achievement:</span>
                  <span className="text-white font-medium">{result.task1_scores.task_achievement}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Coherence & Cohesion:</span>
                  <span className="text-white font-medium">{result.task1_scores.coherence_cohesion}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Lexical Resource:</span>
                  <span className="text-white font-medium">{result.task1_scores.lexical_resource}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Grammar & Accuracy:</span>
                  <span className="text-white font-medium">{result.task1_scores.grammar_accuracy}</span>
                </div>
                <div className="border-t border-slate-600 pt-3">
                  <div className="flex justify-between font-semibold">
                    <span className="text-white">Task 1 Band:</span>
                    <span className="text-blue-400 text-lg">{result.task1_scores.band_score}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task 2 Breakdown */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white text-lg">Task 2 Scores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Task Response:</span>
                  <span className="text-white font-medium">{result.task2_scores.task_achievement}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Coherence & Cohesion:</span>
                  <span className="text-white font-medium">{result.task2_scores.coherence_cohesion}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Lexical Resource:</span>
                  <span className="text-white font-medium">{result.task2_scores.lexical_resource}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300 text-sm">Grammar & Accuracy:</span>
                  <span className="text-white font-medium">{result.task2_scores.grammar_accuracy}</span>
                </div>
                <div className="border-t border-slate-600 pt-3">
                  <div className="flex justify-between font-semibold">
                    <span className="text-white">Task 2 Band:</span>
                    <span className="text-blue-400 text-lg">{result.task2_scores.band_score}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comments Legend */}
            {(task1Comments.length > 0 || task2Comments.length > 0) && (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Feedback Legend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                      <span className="text-slate-300 text-sm">Highlighted text has feedback</span>
                    </div>
                    <p className="text-slate-400 text-xs mt-2">
                      Hover over highlighted text to see detailed comments from your instructor.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentResults;